import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from 'react';

// Track info type
export interface AudioTrack {
  id: string;
  src: string;
  title: string;
  titleFa?: string;
  duration?: number;
  contentType?: string;
  grade?: number;
  course?: string;
  topicNumber?: number;
}

// Audio context type
interface AudioContextType {
  // Current track
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  playbackSpeed: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  playTrack: (track: AudioTrack) => void;
  togglePlay: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaybackSpeed: (speed: number) => void;
  skip: (seconds: number) => void;
  retry: () => void;
  clearError: () => void;
  
  // Mini player visibility
  showMiniPlayer: boolean;
  setShowMiniPlayer: (show: boolean) => void;
}

// Create context
const AudioContext = createContext<AudioContextType | undefined>(undefined);

// R2 CDN base URL
const R2_CDN_URL = 'https://cdn.solarmaktab.com';

// Audio provider props
interface AudioProviderProps {
  children: ReactNode;
}

// Audio provider component
export function AudioProvider({ children }: AudioProviderProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // State
  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
      
      // Event listeners
      audioRef.current.addEventListener('play', () => setIsPlaying(true));
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      audioRef.current.addEventListener('timeupdate', () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      });
      audioRef.current.addEventListener('loadedmetadata', () => {
        if (audioRef.current) {
          setDuration(audioRef.current.duration);
          setIsLoading(false);
        }
      });
      audioRef.current.addEventListener('waiting', () => setIsLoading(true));
      audioRef.current.addEventListener('canplay', () => setIsLoading(false));
      audioRef.current.addEventListener('error', () => {
        setError('Error loading audio');
        setIsLoading(false);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Play a track
  const playTrack = useCallback((track: AudioTrack) => {
    if (!audioRef.current) return;
    
    setError(null);
    setIsLoading(true);
    setCurrentTrack(track);
    setShowMiniPlayer(true);
    
    // Build full URL
    const audioUrl = track.src.startsWith('http') ? track.src : `${R2_CDN_URL}/${track.src}`;
    
    audioRef.current.src = audioUrl;
    audioRef.current.playbackRate = playbackSpeed;
    audioRef.current.volume = isMuted ? 0 : volume;
    
    audioRef.current.play().catch(err => {
      console.error('Error playing audio:', err);
      setError('Error playing audio');
      setIsLoading(false);
    });
  }, [playbackSpeed, volume, isMuted]);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (!audioRef.current || !currentTrack) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Error playing audio');
      });
    }
  }, [isPlaying, currentTrack]);

  // Pause
  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  // Resume
  const resume = useCallback(() => {
    if (audioRef.current && currentTrack) {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Error playing audio');
      });
    }
  }, [currentTrack]);

  // Stop
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentTrack(null);
    setShowMiniPlayer(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  // Seek
  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(audioRef.current.currentTime);
    }
  }, [duration]);

  // Set volume
  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
    
    if (clampedVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
    }
  }, [isMuted]);

  // Set playback speed
  const setPlaybackSpeed = useCallback((speed: number) => {
    setPlaybackSpeedState(speed);
    
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, []);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    if (audioRef.current) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  // Retry loading current track
  const retry = useCallback(() => {
    if (!audioRef.current || !currentTrack) return;
    
    setError(null);
    setIsLoading(true);
    
    // Build full URL
    const audioUrl = currentTrack.src.startsWith('http') ? currentTrack.src : `${R2_CDN_URL}/${currentTrack.src}`;
    
    audioRef.current.src = audioUrl;
    audioRef.current.load();
    audioRef.current.play().catch(err => {
      console.error('Error playing audio:', err);
      setError('Error playing audio');
      setIsLoading(false);
    });
  }, [currentTrack]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AudioContextType = {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackSpeed,
    isLoading,
    error,
    playTrack,
    togglePlay,
    pause,
    resume,
    stop,
    seek,
    setVolume,
    toggleMute,
    setPlaybackSpeed,
    skip,
    retry,
    clearError,
    showMiniPlayer,
    setShowMiniPlayer,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

// Hook to use audio context
export function useAudio(): AudioContextType {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}
